// frontend/src/pages/VerifyEmail.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { getBaseApiUrl, getCurrentTenantCode } from "../utils/tenant";
import "../styles/auth-slider.css";

const VerifyEmail = () => {
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    completeLogin,
    resendVerification: resendVerificationRequest,
  } = useAuth();
  const attemptedTokenRef = useRef(null);

  useEffect(() => {
    const verifyEmail = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");
      const tenant = params.get("tenant") || getCurrentTenantCode();

      if (!token) {
        setVerificationStatus("error");
        setLoading(false);
        return;
      }

      if (attemptedTokenRef.current === token) return;
      attemptedTokenRef.current = token;

      try {
        const apiBaseUrl = getBaseApiUrl();
        const verifyUrl = `${apiBaseUrl}/api/auth/verify-email?token=${encodeURIComponent(token)}&tenantCode=${encodeURIComponent(tenant)}`;

        const response = await fetch(verifyUrl, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Code": tenant,
          },
        });

        const data = await response.json();
        if (response.ok && data.success) {
          const loginResult = await completeLogin(data, {
            preserveGuestCart: true,
          });
          if (!loginResult.success) {
            throw new Error(loginResult.message || "Unable to sign in");
          }

          setVerificationStatus("success");
          toast.success("Email verified. You are now signed in.", {
            id: "email-verification",
          });
          setTimeout(() => {
            navigate("/dashboard", { replace: true });
          }, 1500);
        } else if (data.alreadyVerified) {
          setVerificationStatus("already_verified");
          toast.info("Email already verified. Please sign in.", {
            id: "email-verification",
          });
          setTimeout(() => {
            navigate("/login", { replace: true });
          }, 1500);
        } else {
          setVerificationStatus("error");
          toast.error(data.message || "Verification failed", {
            id: "email-verification",
          });
        }
      } catch (error) {
        console.error("❌ Verification error:", error);
        setVerificationStatus("error");
        toast.error(
          error.message || "An error occurred during verification",
          { id: "email-verification" },
        );
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [location.search, navigate, completeLogin]);

  const resendVerification = async () => {
    const email = localStorage.getItem("pendingVerificationEmail");
    if (!email) {
      toast.error("Please provide your email address");
      return;
    }
    await resendVerificationRequest(email);
  };

  if (loading) {
    return (
      <div className="auth-page">
        <div
          className="auth-card-modern"
          style={{ textAlign: "center", padding: "40px" }}
        >
          <h2>Verifying Email...</h2>
          <div className="loading-spinner" style={{ margin: "20px auto" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div
        className="auth-card-modern"
        style={{ textAlign: "center", padding: "40px", maxWidth: "480px", margin: "0 auto" }}
      >
        {verificationStatus === "success" && (
          <>
            <div
              style={{
                fontSize: "64px",
                color: "#4CAF50",
                marginBottom: "20px",
              }}
            >
              ✓
            </div>
            <h2>Email Verified!</h2>
            <p style={{ color: "#666", marginBottom: "20px" }}>
              Your email has been successfully verified. You can now access all
              features.
            </p>
            <button
              className="btn-submit"
              onClick={() => navigate("/dashboard")}
              style={{ marginTop: "10px" }}
            >
              Go to Dashboard
            </button>
          </>
        )}

        {verificationStatus === "already_verified" && (
          <>
            <div
              style={{
                fontSize: "64px",
                color: "#FFA500",
                marginBottom: "20px",
              }}
            >
              ℹ️
            </div>
            <h2>Already Verified</h2>
            <p style={{ color: "#666", marginBottom: "20px" }}>
              Your email was already verified. Please sign in.
            </p>
            <button
              className="btn-submit"
              onClick={() => navigate("/login")}
              style={{ marginTop: "10px" }}
            >
              Sign In
            </button>
          </>
        )}

        {verificationStatus === "error" && (
          <>
            <div
              style={{
                fontSize: "64px",
                color: "#f44336",
                marginBottom: "20px",
              }}
            >
              ⚠️
            </div>
            <h2>Verification Failed</h2>
            <p style={{ color: "#666", marginBottom: "20px" }}>
              The verification link may be invalid or expired.
            </p>
            <div style={{ 
              marginTop: "20px", 
              display: "flex", 
              gap: "10px", 
              justifyContent: "center", 
              flexWrap: "wrap" 
            }}>
              <button 
                className="btn-submit" 
                onClick={resendVerification}
                style={{ minWidth: "200px" }}
              >
                Resend Verification Email
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;